import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import USER_PATHS from "../USER_PATHS";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.cherrfy.app";
const GOOGLE_PLAY_QR_URL = "https://raw.githubusercontent.com/hawkeye-bd/cherrfy-cdn/main/cherrfy-apk-qr.png";
const CHERRFY_LOGO_URL = "https://raw.githubusercontent.com/hawkeye-bd/cherrfy-cdn/refs/heads/main/logo/cherrfy-logo.png";
const CORPORATE_FONT_FAMILY = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';

const socialLinks = [
  { name: "Facebook", href: "https://www.facebook.com/Cherrfy/", icon: Facebook },
  { name: "Instagram", href: "https://www.instagram.com/cherrfy.store/", icon: Instagram },
  { name: "LinkedIn", href: "https://www.linkedin.com/company/cherrfy", icon: Linkedin },
];

const footerLinks = {
  company: [
    { label: "About Us", href: USER_PATHS.ABOUT },
    { label: "Contact Us", href: USER_PATHS.CONTACT },
    // { label: "Careers", href: "/careers" },
    // { label: "Blog", href: "/blog" },
  ],
  support: [
    { label: "Help Center", href: USER_PATHS.HELP_CENTER },
    { label: "Track Order", href: USER_PATHS.TRACK_ORDER },
    { label: "Returns & Refunds", href: USER_PATHS.RETURNS_REFUNDS },
    { label: "Shipping Info", href: USER_PATHS.SHIPPING_INFO },
  ],
  explore: [
    { label: "All Categories", href: USER_PATHS.CATEGORIES },
    { label: "Electronics", href: "/category/electronics-smart-comfort" },
    { label: "Home & Living", href: "/category/home-living" },
    { label: "Fashion", href: "/category/fashion-comfort-wear" },
    { label: "Beauty", href: "/category/beauty-health-personal-care" },
  ],
  legal: [
    { label: "Terms of Service", href: USER_PATHS.TERMS_OF_SERVICE },
    { label: "Privacy Policy", href: USER_PATHS.PRIVACY_POLICY },
    { label: "Account Deletion", href: USER_PATHS.ACCOUNT_DELETION },
    { label: "Cookie Policy", href: USER_PATHS.COOKIES_POLICY },
  ],
};

const paymentMethods = [
  {
    label: "Visa",
    logo: "https://upload.wikimedia.org/wikipedia/commons/5/5c/Visa_Inc._logo_%282021%E2%80%93present%29.svg",
    logoClassName: "h-2",
  },
  {
    label: "Mastercard",
    logo: "https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg",
    logoClassName: "h-4",
  },
  {
    label: "American Express",
    logo: "https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg",
    logoClassName: "h-4",
  },
  {
    label: "Discover",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e5/DiscoverCard.svg/330px-DiscoverCard.svg.png",
    logoClassName: "h-4",
  },
  {
    label: "Diners Club",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/Diners_Club_Logo3.svg/250px-Diners_Club_Logo3.svg.png",
    logoClassName: "h-4",
  },
  {
    label: "JCB",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/40/JCB_logo.svg/330px-JCB_logo.svg.png",
    logoClassName: "h-4",
  },
  {
    label: "UnionPay",
    logo: "https://upload.wikimedia.org/wikipedia/commons/1/1b/UnionPay_logo.svg",
    logoClassName: "h-4",
  },
  {
    label: "Apple Pay",
    logo: "https://upload.wikimedia.org/wikipedia/commons/b/b0/Apple_Pay_logo.svg",
    logoClassName: "h-4",
  },
  {
    label: "Google Pay",
    logo: "https://upload.wikimedia.org/wikipedia/commons/f/f2/Google_Pay_Logo.svg",
    logoClassName: "h-4",
  },
  {
    label: "PayPal",
    logo: "https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg",
    logoClassName: "h-4",
  },
  // {
  //   label: "bKash",
  //   logo: "https://www.logo.wine/a/logo/BKash/BKash-bKash-Logo.wine.svg",
  //   logoClassName: "h-8",
  // },
  // {
  //   label: "Nagad",
  //   logo: "https://download.logo.wine/logo/Nagad/Nagad-Logo.wine.png",
  //   logoClassName: "h-8",
  // },
];

const Footer = () => {
  const year = useMemo(() => new Date().getFullYear(), []);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);

  useEffect(() => {
    if (!isQrModalOpen) return undefined;
    const onEscape = (event) => {
      if (event.key === "Escape") setIsQrModalOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onEscape);
    };
  }, [isQrModalOpen]);

  const qrModal = isQrModalOpen
    ? createPortal(
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-slate-900/45 p-4 backdrop-blur-[1px]"
          onClick={() => setIsQrModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Scan QR code"
            onClick={(event) => event.stopPropagation()}
            className="my-auto w-full max-w-xs border border-slate-200/70 bg-white p-4 shadow-2xl"
          >
            <p className="text-sm font-medium text-slate-900">Scan to install Cherrfy</p>
            <p className="mt-1 text-xs text-slate-500">Open Google Play and install in seconds.</p>

            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-3 block border border-slate-200/70 p-2 transition-colors hover:border-[#D2042D]/50"
            >
              <img
                src={GOOGLE_PLAY_QR_URL}
                alt="Zoomed Cherrfy Google Play QR code"
                className="mx-auto aspect-square max-h-[52vh] w-full max-w-56 border border-slate-200/70 object-contain"
              />
            </a>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-10 items-center justify-center rounded-md bg-slate-900 px-3 py-2 text-center text-xs font-medium text-white transition-colors hover:bg-black"
              >
                Open Play Store
              </a>
              <button
                type="button"
                onClick={() => setIsQrModalOpen(false)}
                className="inline-flex min-h-10 items-center justify-center rounded-md border border-slate-200/70 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <footer className="mt-auto border-t border-slate-200/70 bg-white" style={{ fontFamily: CORPORATE_FONT_FAMILY }}>
      <div className="mx-auto w-full max-w-[1680px] px-4 py-8 sm:px-5 lg:px-7 xl:px-12 2xl:px-[68px]">
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-12 xl:gap-10">
          <div className="xl:col-span-4">
            <Link to="/" className="inline-flex items-center" aria-label="Cherrfy home">
              <span className="inline-flex h-[29px] w-[102px] items-center overflow-hidden lg:h-8 lg:w-[114px]">
                <img src={CHERRFY_LOGO_URL} className="h-full w-full object-cover object-center" alt="Cherrfy" />
              </span>
            </Link>

            <p className="mt-3 max-w-sm text-sm leading-6 text-slate-600">
              Cherrfy enables reliable multi-market commerce operations with simplified buying, tracking, and support.
            </p>

            <div className="mt-4 space-y-2.5 text-sm">
              <p className="flex items-start gap-2 text-slate-600">
                <MapPin size={15} className="mt-[2px] shrink-0 text-slate-500" />
                <span>30 N Gould St Suite R, Sheridan, WY, United States, 82801</span>
              </p>
              <a
                href="mailto:inquiry@cherrfy.com"
                className="flex items-start gap-2 text-slate-600 transition-colors hover:text-[#D2042D]"
              >
                <Mail size={15} className="mt-[2px] shrink-0 text-slate-500" />
                inquiry@cherrfy.com
              </a>
              <a
                href="tel:+13073938600"
                className="flex items-start gap-2 text-slate-600 transition-colors hover:text-[#D2042D]"
              >
                <Phone size={15} className="mt-[2px] shrink-0 text-slate-500" />
                +13073938600 
              </a>
            </div>

            <div className="mt-4 flex items-center gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={social.name}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200/70 bg-white text-slate-700 transition-colors hover:border-[#D2042D]/50 hover:text-[#D2042D]"
                >
                  <social.icon size={15} />
                </a>
              ))}
            </div>
          </div>

          <div className="xl:col-span-8">
            <div className="grid grid-cols-2 gap-x-5 gap-y-6 md:grid-cols-4 md:gap-x-6">
              <FooterCol title="Explore" items={footerLinks.explore} />
              <FooterCol title="Support" items={footerLinks.support} />
              <FooterCol title="Company" items={footerLinks.company} />
              <FooterCol title="Legal" items={footerLinks.legal} />
            </div>

            <div className="mt-6 border border-slate-200/70 bg-white p-3.5 sm:p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">Get Cherrfy App</p>
                  <p className="text-xs text-slate-500">Quick access to orders, updates, and checkout.</p>
                </div>

                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                  <a
                    href={GOOGLE_PLAY_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-md bg-slate-900 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-black"
                  >
                    Google Play
                  </a>
                  <button
                    type="button"
                    onClick={() => setIsQrModalOpen(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200/70 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-[#D2042D]/50 hover:text-[#D2042D]"
                  >
                    <img
                      src={GOOGLE_PLAY_QR_URL}
                      alt="Cherrfy Google Play QR code"
                      className="h-4 w-4 rounded border border-slate-200/70"
                    />
                    Scan QR
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 border border-slate-200/70 bg-white p-3.5 sm:p-4">
              <div className="flex flex-col gap-3   ">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">Our payment methods</p>
                  <p className="text-xs text-slate-500">We accept a wide range of payment methods.</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {paymentMethods.map((method) => (
                    <PaymentLogo key={method.label} method={method} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {qrModal}

      <div className="border-t border-slate-200/70 bg-white">
        <div className="mx-auto flex w-full max-w-[1680px] flex-col gap-2 px-4 py-4 text-xs sm:px-5 md:flex-row md:items-center md:justify-between lg:px-7 xl:px-12 2xl:px-[68px]">
          <div className="flex flex-wrap items-center gap-2 text-slate-500">
            <span>© {year} Cherrfy LLC</span>
            <span className="text-slate-300">•</span>
            <Link to={USER_PATHS.TERMS_OF_SERVICE} className="transition-colors hover:text-[#D2042D]">
              Terms
            </Link>
            <span className="text-slate-300">•</span>
            <Link to={USER_PATHS.PRIVACY_POLICY} className="transition-colors hover:text-[#D2042D]">
              Privacy
            </Link>
          </div>
          <p className="text-slate-500">
            DBID: <span className="font-medium text-slate-700">631897667</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

function FooterCol({ title, items }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">{title}</p>
      <div className="mt-2.5 space-y-1.5">
        {items.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            className="group flex w-full items-center justify-between gap-1.5 text-sm font-normal text-slate-700 transition-colors hover:text-[#D2042D]"
          >
            <span className="truncate">{item.label}</span>
            <ChevronRight size={14} className="opacity-0 transition-opacity group-hover:opacity-100" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function PaymentLogo({ method }) {
  const Icon = method.icon;

  return (
    <span
      title={method.label}
      aria-label={method.label}
      className="inline-flex h-9 min-w-[76px] items-center justify-center rounded-md border border-slate-200/80 bg-white px-2.5"
    >
      {method.logo ? (
        <img
          src={method.logo}
          alt={method.label}
          loading="lazy"
          referrerPolicy="no-referrer"
          className={["max-h-8 max-w-[88px] object-contain", method.logoClassName || ""].join(" ")}
        />
      ) : (
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold leading-none text-slate-700">
          {Icon ? <Icon size={14} strokeWidth={2.1} aria-hidden="true" /> : null}
          {method.shortLabel || method.label}
        </span>
      )}
    </span>
  );
}

export default Footer;
